'use client';

import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import { GroqImage } from '@/helpers/Image';
import { PagePricingSchema } from '@/sanity/pages/PagePricingTemplate';
import Image from 'next/image';
import heroMobileBackground from '@/public/global/hero-mobile-background.png';
import heroDesktopBackground from '@/public/global/hero_desktop_background-lg-2.png';

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function HeroSection({ data }: { data: PagePricingSchema }) {
  return (
    <section className="relative w-full pt-[9rem] pb-[9rem] lg:pt-[14rem] lg:pb-[10rem]">
      <motion.div
        initial={{ opacity: 0, filter: 'blur(8px)' }}
        animate={{ opacity: 1, filter: 'blur(0px)' }}
        transition={{ duration: 1.2, ease }}
        className="absolute w-full h-full top-[0rem] lg:hidden block z-0 pointer-events-none"
      >
        <Image
          src={heroMobileBackground}
          alt=""
          className="w-full h-full object-cover object-center"
        />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, filter: 'blur(8px)' }}
        animate={{ opacity: 1, filter: 'blur(0px)' }}
        transition={{ duration: 1.2, ease }}
        className="absolute w-full h-[135rem] top-[0rem] hidden lg:block z-0 pointer-events-none"
      >
        <Image
          src={heroDesktopBackground}
          alt=""
          className="w-full h-full object-cover object-center"
        />
      </motion.div>
      {data?.heroBackgroundImage && (
        <div className="pointer-events-none hidden lg:block absolute w-full h-[130rem] left-0 top-0 z-0">
          <GroqImage
            src={data.heroBackgroundImage}
            alt=""
            className="!h-full !w-full object-cover object-bottom opacity-35"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-transparent to-white/80" />
        </div>
      )}

      <div className="relative z-10 mx-auto flex w-[min(170.5rem,calc(100%-4rem))] lg:w-[min(170.5rem,calc(100%-8rem))] flex-col items-center gap-[2.5rem] text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="flex flex-col items-center gap-[1.5rem]"
        >
          {data?.heroChip && (
            <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-black">
              {data.heroChip}
            </p>
          )}
          {data?.heroTitle && (
            <div className="richtext prose-headings:text-black prose-p:text-[2rem] prose-p:leading-[1.5] prose-p:text-black/70 max-w-[101.25rem] flex flex-col gap-[2rem]">
              <PortableText
                value={data.heroTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </motion.div>

        {data?.heroCTA && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.2 }}
            className="w-full lg:w-fit"
          >
            <CTAButton data={data.heroCTA} />
          </motion.div>
        )}
      </div>
    </section>
  );
}
